import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { permissionsAPI } from '../services/api';

const PermissionContext = createContext(null);

// Permission types
export const PERMISSION_TYPES = {
  ALLOW: 'ALLOW',
  DENY: 'DENY',
};

// Resource types
export const RESOURCE_TYPES = {
  PROJECT: 'project',
  TASK: 'task',
  SUBTASK: 'subtask',
};

const SUBJECT_TYPES = {
  USER: 'user',
  GROUP: 'group',
};

const getPermissionSubject = (permission) => {
  const userId = permission.userId || permission.user_id || null;
  const groupId = permission.groupId || permission.group_id || null;

  if (groupId) {
    return { subjectType: SUBJECT_TYPES.GROUP, subjectId: groupId };
  }

  return { subjectType: SUBJECT_TYPES.USER, subjectId: userId };
};

export const PermissionProvider = ({ children }) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [resourceHierarchy, setResourceHierarchy] = useState({});

  // Register a new resource in hierarchy (called when creating tasks/subtasks)
  const registerResource = useCallback((resourceId, parentType, parentId) => {
    setResourceHierarchy((prev) => ({
      ...prev,
      [resourceId]: { parentType, parentId },
    }));
  }, []);

  // Get parent resource info
  const getParentResource = useCallback((resourceType, resourceId) => {
    if (resourceType === RESOURCE_TYPES.PROJECT) {
      return null; // Projects have no parent
    }
    return resourceHierarchy[resourceId] || null;
  }, [resourceHierarchy]);

  const matchesSubject = useCallback((permission, subjectId, subjectType = SUBJECT_TYPES.USER) => {
    const subject = getPermissionSubject(permission);
    return subject.subjectType === subjectType && subject.subjectId === subjectId;
  }, []);

  // Get direct permission for a specific user and resource
  const getDirectPermission = useCallback((subjectId, resourceType, resourceId, subjectType = SUBJECT_TYPES.USER) => {
    const permission = permissions.find(
      (p) => matchesSubject(p, subjectId, subjectType)
        && p.resourceType === resourceType
        && p.resourceId === resourceId
    );
    return permission ? permission.permission : null;
  }, [permissions, matchesSubject]);

  // Check if user has access to a resource (Windows-style logic)
  // DENY always wins over ALLOW
  // Check from current level up to parent
  const hasPermission = useCallback((resourceType, resourceId, subjectId = null, subjectType = SUBJECT_TYPES.USER) => {
    const checkSubjectId = subjectId || user?.id?.toString();
    if (!checkSubjectId) return false;

    // Admin has access to everything
    if (subjectType === SUBJECT_TYPES.USER && user?.role === 'admin' && checkSubjectId === user?.id?.toString()) {
      return true;
    }

    // Check for DENY first at any level (DENY wins)
    const checkDeny = (rType, rId) => {
      // Check current resource
      const directPermission = getDirectPermission(checkSubjectId, rType, rId, subjectType);
      if (directPermission === PERMISSION_TYPES.DENY) {
        return true; // Found DENY
      }

      // Check parent resource
      const parent = getParentResource(rType, rId);
      if (parent) {
        return checkDeny(parent.parentType, parent.parentId);
      }

      return false; // No DENY found
    };

    // If any DENY exists in hierarchy, access is blocked
    if (checkDeny(resourceType, resourceId)) {
      return false;
    }

    // Check for ALLOW (current level or any parent)
    const checkAllow = (rType, rId) => {
      // Check current resource
      const directPermission = getDirectPermission(checkSubjectId, rType, rId, subjectType);
      if (directPermission === PERMISSION_TYPES.ALLOW) {
        return true; // Found ALLOW
      }

      // Check parent resource (inheritance)
      const parent = getParentResource(rType, rId);
      if (parent) {
        return checkAllow(parent.parentType, parent.parentId);
      }

      return false; // No ALLOW found
    };

    return checkAllow(resourceType, resourceId);
  }, [user, getDirectPermission, getParentResource]);

  // Get all permissions for a resource
  const getResourcePermissions = useCallback((resourceType, resourceId) => {
    return permissions.filter(
      (p) => p.resourceType === resourceType && p.resourceId === resourceId
    );
  }, [permissions]);

  // Get all permissions for a user
  const getUserPermissions = useCallback((subjectId, subjectType = SUBJECT_TYPES.USER) => {
    return permissions.filter((p) => matchesSubject(p, subjectId, subjectType));
  }, [permissions, matchesSubject]);

  // Set permission for a user on a resource
  const setPermission = useCallback((subjectId, resourceType, resourceId, permissionType, subjectType = SUBJECT_TYPES.USER) => {
    setPermissions((prev) => {
      // Remove existing permission if any
      const filtered = prev.filter(
        (p) => !(matchesSubject(p, subjectId, subjectType)
          && p.resourceType === resourceType
          && p.resourceId === resourceId)
      );

      // Add new permission if not removing
      if (permissionType) {
        return [
          ...filtered,
          {
            userId: subjectType === SUBJECT_TYPES.USER ? subjectId : null,
            groupId: subjectType === SUBJECT_TYPES.GROUP ? subjectId : null,
            resourceType,
            resourceId,
            permission: permissionType,
          },
        ];
      }

      return filtered;
    });
  }, [matchesSubject]);

  // Remove permission
  const removePermission = useCallback((subjectId, resourceType, resourceId, subjectType = SUBJECT_TYPES.USER) => {
    setPermissions((prev) =>
      prev.filter(
        (p) => !(matchesSubject(p, subjectId, subjectType)
          && p.resourceType === resourceType
          && p.resourceId === resourceId)
      )
    );
  }, [matchesSubject]);

  // Get effective permission (considering hierarchy)
  const getEffectivePermission = useCallback((subjectId, resourceType, resourceId, subjectType = SUBJECT_TYPES.USER) => {
    // Check for direct DENY first
    const checkForDeny = (rType, rId) => {
      const directPerm = getDirectPermission(subjectId, rType, rId, subjectType);
      if (directPerm === PERMISSION_TYPES.DENY) return true;

      const parent = getParentResource(rType, rId);
      if (parent) return checkForDeny(parent.parentType, parent.parentId);

      return false;
    };

    if (checkForDeny(resourceType, resourceId)) {
      return { permission: PERMISSION_TYPES.DENY, inherited: false, source: 'hierarchy' };
    }

    // Check for direct permission
    const directPerm = getDirectPermission(subjectId, resourceType, resourceId, subjectType);
    if (directPerm) {
      return { permission: directPerm, inherited: false, source: 'direct' };
    }

    // Check inherited permission
    const parent = getParentResource(resourceType, resourceId);
    if (parent) {
      const parentPerm = getDirectPermission(subjectId, parent.parentType, parent.parentId, subjectType);
      if (parentPerm === PERMISSION_TYPES.ALLOW) {
        return { permission: PERMISSION_TYPES.ALLOW, inherited: true, source: 'parent' };
      }

      // Check grandparent
      const grandparent = getParentResource(parent.parentType, parent.parentId);
      if (grandparent) {
        const grandparentPerm = getDirectPermission(subjectId, grandparent.parentType, grandparent.parentId, subjectType);
        if (grandparentPerm === PERMISSION_TYPES.ALLOW) {
          return { permission: PERMISSION_TYPES.ALLOW, inherited: true, source: 'grandparent' };
        }
      }
    }

    return { permission: null, inherited: false, source: null };
  }, [getDirectPermission, getParentResource]);

  const loadSubjectPermissions = useCallback(async ({ companyId, subjectType, subjectId }) => {
    const response = await permissionsAPI.listBySubject({ companyId, subjectType, subjectId });
    const items = Array.isArray(response?.items) ? response.items : [];

    setPermissions(
      items.map((item) => ({
        id: item.id,
        userId: item.userId || item.user_id || null,
        groupId: item.groupId || item.group_id || null,
        resourceType: item.resourceType || item.resource_type,
        resourceId: item.resourceId || item.resource_id,
        permission: item.permission,
      }))
    );
  }, []);

  const saveSubjectPermissions = useCallback(async ({ companyId, subjectType, subjectId }) => {
    const assignments = permissions
      .filter((p) => matchesSubject(p, subjectId, subjectType))
      .map((p) => ({
        resourceType: p.resourceType,
        resourceId: p.resourceId,
        permission: p.permission,
      }));

    return permissionsAPI.bulkReplace({
      companyId,
      subjectType,
      subjectId,
      assignments,
    });
  }, [permissions, matchesSubject]);

  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setResourceHierarchy({});
  }, []);

  const value = useMemo(
    () => ({
      permissions,
      hasPermission,
      getResourcePermissions,
      getUserPermissions,
      setPermission,
      removePermission,
      getEffectivePermission,
      getDirectPermission,
      registerResource,
      loadSubjectPermissions,
      saveSubjectPermissions,
      clearPermissions,
      PERMISSION_TYPES,
      RESOURCE_TYPES,
      SUBJECT_TYPES,
    }),
    [
      permissions,
      hasPermission,
      getResourcePermissions,
      getUserPermissions,
      setPermission,
      removePermission,
      getEffectivePermission,
      getDirectPermission,
      registerResource,
      loadSubjectPermissions,
      saveSubjectPermissions,
      clearPermissions,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
